<template>
  <div>
    <form-bread-crumb :title="'User Details'" />
    <unprivilege-panel v-if="getterUnprivilegePage === 'UserDetails'" :css="'details-panel'" />

    <div class="user-details" v-else>
      <toggle-status-confirmation v-if="getterPopupName === 'UserStatusConfirmation'"
                        :statusOptions="statusOptions" @updateStatus="(val) => user.status = val" />
      <resend-invitation v-else-if="getterPopupName === 'ResendInvitation'"
                        :inviteOptions="inviteOptions" />
      <delete-user-confirmation v-else-if="getterPopupName === 'DeleteUser'"
                        :userData="user" />
      <user-2FA v-else-if="getterPopupName === 'User2FA'" :tfaValue="tfaValue"
                :user="user" @refreshTFA="refreshTFA" />

      <div class="actions" v-if="isCreateAction">
        <button class="primary-btn" @click="triggerAction('Create')">
          <i class="fa fa-plus" />
          Create
        </button>
      </div>

      <div class="actions" v-else-if="user.manageAccess">
        <button class="primary-btn cancel" v-if="!disableData" @click="resetData">
          <i class="fa fa-times" />
          Cancel
        </button>
        <button class="dark-btn" v-if="!disableData" @click="triggerAction('Update')">
          <i class="fa fa-save" />
          Save
        </button>
        <button class="dark-btn" v-else @click="disableData = false">
          <i class="fa fa-pencil-alt" />
          Edit
        </button>
        <button class="primary-btn delete" @click="deleteUser">
          <i class="fa fa-trash" />
          Delete
        </button>
      </div>

      <error-info :errorList="formErrors.user" class="main-error" />

      <div v-if="isCreateAction">
        <md-field class="no-m-top" :class="{'input-error': $v.user.email.$error || formErrors.email.length}">
          <label>Email</label>
          <md-input v-model="user.email" :ref="'Email'" @blur="$v.user.email.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.user.email.$error && !$v.user.email.required">Please enter user email</h6>
        <h6 class="error-message" v-else-if="$v.user.email.$error && !$v.user.email.email">Please enter a valid email</h6>
        <error-info v-else :errorList="formErrors.email" />
      </div>

      <div v-else>
        <div class="user-field">
          <h5>Email</h5>:
          <p class="break">{{user.email}}</p>
        </div>

        <div class="user-field">
          <h5>Verified</h5>:
          <p>{{user.verified ? 'Yes' : 'Pending'}}</p>
          <u v-if="user.manageAccess && !user.verified && user.status" @click="resendInvitation">Resend Invitation</u>
        </div>

        <div class="user-field">
          <h5>2FA</h5>:
          <p v-if="tfaMsg">
            Enabled
            <i class="fa fa-info-circle">
              <md-tooltip md-direction="top">{{tfaMsg}}</md-tooltip>
            </i>
          </p>
          <md-switch v-else-if="user.manageAccess" v-model="user.tfa_enabled" @change="toggleTFA" />
          <p v-else>{{user.tfa_enabled ? 'Yes' : 'No'}}</p>

          <u v-if="user.manageAccess && user.tfa_enabled" @click="resetTFA">
            <i class="fa fa-redo" style="margin-right: 10px;" />Reset
          </u>
        </div>

        <div class="user-field">
          <h5>Active</h5>:
          <md-switch v-if="user.manageAccess" v-model="user.status" @change="statusConfirmation" />
          <p v-else>{{user.status ? 'Yes' : 'No'}}</p>
        </div>
      </div>

      <md-field :class="{'input-error': $v.user.name.$error || formErrors.name.length}">
        <label>Name</label>
        <md-input v-model="user.name" :disabled="disableData" @blur="$v.user.name.$touch()" />
      </md-field>
      <h6 class="error-message" v-if="$v.user.name.$error && !$v.user.name.required">Please enter a user name</h6>
      <h6 class="error-message" v-else-if="$v.user.name.$error && !$v.user.name.alphaSpace">Please enter a valid user name with alphabets &amp; space only</h6>
      <error-info v-else :errorList="formErrors.name" />

      <div v-if="user.manageAccess">
        <md-field id="RoleOptions" :class="{'input-error': $v.role.id.$error || formErrors.role.length}">
          <label>Role</label>
          <u v-if="role.manageAccess" @click="createRole">(Create new Role)</u>
          <md-select v-model="role.id" @md-opened="setDropdownPosOffset('RoleOptions')"
                    :disabled="disableData" @blur="$v.role.id.$touch()">
            <md-option v-for="(data, index) in roleOptions" :key="index" :value="data.id"
                      :class="{'highlight-option': data.id === 'NEW'}">
              {{data.name}}
            </md-option>
          </md-select>
        </md-field>
        <h6 class="error-message" v-if="$v.role.id.$error">Please assign a role for user</h6>
        <error-info v-else :errorList="formErrors.role" />
      </div>

      <md-field v-else>
        <label>Role</label>
        <md-input v-model="role.name" disabled />
      </md-field>

      <strong>
        Privileges
        <span v-if="role.id">Role - {{role.name}}</span>
      </strong>
      <privilege-list :selectedPrivileges="role.privilegeList"
                :self-va-enabled="selfVaEnabled" />

      <strong v-if="!isCreateAction">
        Active Sessions <span v-if="sessionsAdded">({{userSessions.length}})</span>
      </strong>
      <user-sessions v-if="!isCreateAction" :userSessions="userSessions" :user="user"
                    @refreshUserSessions="(newVal) => userSessions = newVal"
                    :sessionsAdded="sessionsAdded" />
    </div>
  </div>
</template>

<script>
import FormBreadCrumb from '@/components/global/FormBreadCrumb'
import ErrorInfo from '@/components/global/ErrorInfo'
import UnprivilegePanel from '@/components/global/UnprivilegePanel'
import PrivilegeList from '@/components/Roles/PrivilegeList'
import ToggleStatusConfirmation from '@/components/Users/ToggleStatusConfirmation'
import ResendInvitation from '@/components/Users/ResendInvitation'
import DeleteUserConfirmation from '@/components/Users/DeleteUserConfirmation'
import User2FA from '@/components/TFA/User2FA'
import UserSessions from '@/components/Settings/UserProfile/UserSessions'

import ApiServices from '@/services/api/users'
import { required, email } from 'vuelidate/lib/validators'
import { alphaSpace } from '@/utils/helpers/validators'
import { setDropdownPosOffset, openPopup, checkFormErrors, clearFormErrors } from '@/utils/helpers'
import { mapGetters } from 'vuex'

export default {
  name: 'UserDetails',
  components: { FormBreadCrumb, PrivilegeList, ToggleStatusConfirmation, ResendInvitation, DeleteUserConfirmation, ErrorInfo, UnprivilegePanel, User2FA, UserSessions },
  data () {
    return {
      isCreateAction: false,
      user: {},
      role: {},
      userSessions: [],
      roleOptions: [],
      tfaValue: false,
      tfaMsg: null,
      disableData: true,
      formErrors: {
        email: [],
        user: [],
        name: [],
        role: []
      },
      statusOptions: {},
      inviteOptions: {},
      backupData: {},
      sessionsAdded: false,
      selfVaEnabled: false
    }
  },
  validations: {
    user: {
      name: {
        required,
        alphaSpace
      },
      email: {
        required,
        email
      }
    },
    role: {
      id: {
        required
      }
    }
  },
  watch: {
    'role.id' (newVal) {
      if (this.roleOptions && this.roleOptions.length) {
        let currentRole = this.roleOptions.find(data => data.id === newVal)
        this.role.privilegeList = currentRole.privileges.slice()
        this.role.name = currentRole.name
        this.formErrors['role'] = []
        this.formErrors['user'] = []
      }
    },
    'user.name' () {
      this.formErrors['name'] = []
    },
    'user.email' () {
      this.formErrors['email'] = []
    },
    '$route': {
      handler: 'decideAction',
      immediate: true
    }
  },
  computed: {
    ...mapGetters(['getterPopupName', 'getterUnprivilegePage'])
  },
  beforeDestroy () {
    this.openPopup(null)
  },
  methods: {
    openPopup,
    setDropdownPosOffset,
    checkFormErrors,
    clearFormErrors,
    decideAction () {
      if (this.$route.name === 'UserDetails') {
        this.isCreateAction = false
        this.disableData = true
        this.populateData()
      } else {
        this.isCreateAction = true
        this.disableData = false
        this.checkUserManageAccess()
      }
    },
    populateData () {
      ApiServices.userDetails(this.$route.params.id).then(response => {
        let data = response.data
        if (data) {
          let user = data.user
          let role = user.role
          let privileges = response.privileges
          this.role = {
            id: role.id,
            name: role.name,
            privilegeList: role.privileges,
            manageAccess: privileges.manage_roles
          }
          this.user = {
            id: user.id,
            email: user.email,
            verified: user.verified,
            name: user.name,
            status: user.status === 'ACTIVE',
            tfa_enabled: user.tfa_enabled,
            manageAccess: privileges.manage_users
          }
          this.tfaValue = user.tfa_enabled
          let partner = data.partner
          this.tfaMsg = partner.tfa_required ? partner.tfa_required_message : null
          this.userSessions = data.sessions
          this.sessionsAdded = true
          this.selfVaEnabled = data.self_va_enabled

          this.roleOptions = data.roles
          this.backupData = {
            userName: this.user.name,
            roleId: this.role.id,
            roleName: this.role.name
          }
        }
      }).catch((error) => {
        if (error.status === 404) {
          this.$router.push({name: 'UsersList'})
        }
      })
    },
    checkUserManageAccess () {
      ApiServices.newUserAccess().then(response => {
        let privileges = response.privileges
        if (privileges) {
          this.user = {
            manageAccess: privileges.manage_users
          }
          this.role = {
            manageAccess: privileges.manage_roles
          }

          let data = response.data
          this.roleOptions = data.roles
          this.selfVaEnabled = data.self_va_enabled
          this.focusField()
        }
      })
    },
    focusField () {
      this.$refs.Email.$el.focus()
    },
    createRole () {
      this.$router.push({name: 'RoleCreate'})
    },
    triggerAction (type) {
      this.$v.$touch()
      if (!this.$v.$error && !this.checkFormErrors(this.formErrors)) {
        let params = this.createParams(type)
        type === 'Create' ? this.createUser(params) : this.updateUser(params)
      }
    },
    createParams (type) {
      let params = {
        name: this.user.name,
        role_id: this.role.id
      }

      if (type === 'Create') {
        params['email'] = this.user.email
      }
      return params
    },
    createUser (params) {
      ApiServices.createUser(params).then(response => {
        this.handleToast(response)
        this.$router.replace({name: 'UserDetails', params: {id: response.data.id}})
      }).catch(error => {
        this.handleError(error)
      })
    },
    updateUser (params) {
      ApiServices.updateUser(this.user.id, params).then(response => {
        this.disableData = response.success
        this.handleToast(response)
      }).catch(error => {
        this.handleError(error)
      })
    },
    handleError (response) {
      this.formErrors = {
        ...this.formErrors,
        ...response.body.reason
      }
    },
    deleteUser () {
      this.openPopup('DeleteUser')
    },
    handleToast (response) {
      response.success ? this.successToastMsg(response.message) : this.errorToastMsg(response.message)
    },
    resetData () {
      this.$v.$reset()
      this.disableData = true
      this.user.name = this.backupData.userName
      this.role.id = this.backupData.roleId
      this.role.name = this.backupData.roleName
      this.clearFormErrors(this.formErrors)
    },
    statusConfirmation () {
      this.openPopup('UserStatusConfirmation')
      let status = this.user.status ? 'ACTIVATE' : 'DEACTIVATE'
      this.user.status = !this.user.status

      this.statusOptions = {
        status,
        id: this.user.id
      }
    },
    resendInvitation () {
      this.openPopup('ResendInvitation')
      this.inviteOptions = {
        id: this.user.id,
        email: this.user.email
      }
    },
    toggleTFA () {
      this.user.tfa_enabled = !this.user.tfa_enabled
      this.tfaValue = !this.user.tfa_enabled
      this.openPopup('User2FA')
    },
    resetTFA () {
      this.tfaValue = 'Reset'
      this.openPopup('User2FA')
    },
    refreshTFA (updatedVal) {
      this.user.tfa_enabled = updatedVal
      this.openPopup(null)
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/mixins.scss';

  .user-details {
    background: $white;
    border-radius: 15px;
    position: relative;

    .main-error {
      margin-bottom: 5px;
    }

    .actions {
      button {
        min-width: 100px;
        margin-left: 10px;

        i {
          margin-right: 5px;
        }

        &.cancel {
          background: $grey-text !important;
        }
        &.delete {
          background: $error !important;
        }
      }
    }

    strong {
      @include normal-text($secondary, 14px, 500);
      text-align: left;
      display: block;
      margin: 15px 0 10px;

      span {
        margin-left: 10px;
        padding-left: 10px;
        border-left: 2px solid $grey-outline;
        color: $grey-text;
      }
    }
    u {
      @include normal-text($primary, 13px, 500);
      cursor: pointer;
    }

    .user-field {
      @include custom-flex(flex-start);
      text-align: left;
      margin: 0 0 15px 0;

      h5 {
        @include normal-text($grey-text, 15px, 500);
        min-width: 100px;
      }
      p {
        @include normal-text($secondary, 15px, 500);
        margin-left: 10px;

        i {
          cursor: pointer;

          &.fa-info-circle {
            color: $primary;
          }
        }
      }
      u {
        font-size: 15px !important;
        margin-left: 10px;
        padding-left: 10px;
        border-left: 2px solid $grey-outline;
      }
    }

    .md-field {
      &.no-m-top {
        margin-top: 0 !important;
      }
    }

    .break {
      word-break: break-all;
    }
  }

  .desktop {
    .user-details {
      width: 60%;
      padding: 20px;

      .actions {
        float: right;
      }

      .md-field {
        width: 45%;
      }
    }
  }

  .mobile, .tablet {
    .user-details {
      padding: 10px;

      .actions {
        text-align: right;
        margin-bottom: 10px;
      }

      .md-field {
        max-width: 350px;
      }
    }
  }
</style>
